import {
    type App,
    type DateRange,
    type Employee,
    GetAbsencesParams,
    GetAvailabilitiesParams,
    GetDailyResultsParams,
    GetRosterNotesParams,
    GetRosterTargetsParams,
    GetSchoolHolidaysParams,
    TimeEntryType,
    type Venue,
    dateAdd,
    getRange,
} from "@pentacode/core";
import type { QueryClient } from "@tanstack/query-core";

export const rosterKey = ["roster"] as const;

const ENTRY_TYPES = [
    TimeEntryType.Work,
    TimeEntryType.Vacation,
    TimeEntryType.Sick,
    TimeEntryType.Free,
    TimeEntryType.CompDay,
    TimeEntryType.ChildSick,
    TimeEntryType.SickInKUG,
    TimeEntryType.HourAdjustment,
];

function venueEmployeeIds(app: App, venue: Venue) {
    const worksInDepartment = (e: Employee, departmentId: number) =>
        e.positions.some((p) => p.departmentId === departmentId);
    const departments = venue.departments.filter((department) => app.hasAccess({ department }));
    return app.employees.filter((e) => departments.some((d) => worksInDepartment(e, d.id))).map((e) => e.id);
}

/**
 * Everything the roster needs from the server for one venue and range. Time
 * entries come back resolved against pending local writes at fetch time, so
 * cached copies are marked stale whenever time entries sync (see init.ts).
 */
export function rosterQuery(app: App, venue: Venue, { from, to }: DateRange) {
    const employeeIds = venueEmployeeIds(app, venue);

    return {
        queryKey: [...rosterKey, venue.id, from, to, employeeIds] as const,
        queryFn: async ({ signal }: { signal: AbortSignal }) => {
            const currMonth = getRange(from, "month");
            const nextMonth = getRange(to, "month");
            const twoWeeksPrior = dateAdd(from, { days: -14 });
            const entriesFrom = twoWeeksPrior < currMonth.from ? twoWeeksPrior : currMonth.from;

            const targets = app.api.getRosterTargets(new GetRosterTargetsParams({ from, to, venue: venue.id }), {
                signal,
            });
            const absences = app.api.getAbsences(new GetAbsencesParams({ from, to, employee: employeeIds }), {
                signal,
            });
            const schoolHolidays = app.api.getSchoolHolidays(
                new GetSchoolHolidaysParams({ from, to, country: app.company.country, states: [venue.state] }),
                { signal }
            );
            const availabilities = app.api.getAvailabilities(
                new GetAvailabilitiesParams({ from, to, employees: employeeIds }),
                { signal }
            );
            const dailyResults = app.api.getRosterDailyResults(
                new GetDailyResultsParams({
                    from,
                    to: dateAdd(to, { days: 1 }),
                    filters: employeeIds.map((id) => ({ type: "employeeId" as const, value: id })),
                    planned: true,
                }),
                { signal }
            );
            const notes = app.api.getRosterNotes(new GetRosterNotesParams({ from, to }), { signal });

            // Historical employees who still have work entries at this venue (e.g. withdrawn positions).
            const discoveryIds = await app.getRosterVenueEmployeeIds({
                from: dateAdd(entriesFrom, { days: -14 }),
                to: nextMonth.to,
                type: [TimeEntryType.Work],
                venue: venue.id,
            });
            const known = new Set(employeeIds);
            const allEmployeeIds = [...employeeIds, ...discoveryIds.filter((id) => !known.has(id))];

            const entries = app.getRosterTimeEntries({
                from: entriesFrom,
                to: nextMonth.to,
                type: ENTRY_TYPES,
                includeDeleted: true,
                includeUnassigned: true,
                employee: allEmployeeIds,
            });
            // Only for frequent-times suggestions; never holds up the roster.
            const previousEntries = app
                .getRosterTimeEntries({
                    from: dateAdd(entriesFrom, { days: -14 }),
                    to: entriesFrom,
                    type: ENTRY_TYPES,
                    includeDeleted: true,
                    includeUnassigned: true,
                    employee: allEmployeeIds,
                })
                .catch(() => []);
            const notesOrEmpty = notes.catch((e: unknown) => {
                console.error(e);
                return [];
            });

            return {
                targets: await targets,
                absences: await absences,
                schoolHolidays: await schoolHolidays,
                availabilities: await availabilities,
                dailyResults: await dailyResults,
                entries: await entries,
                notes: await notesOrEmpty,
                previousEntries,
            };
        },
    };
}

export type RosterData = Awaited<ReturnType<ReturnType<typeof rosterQuery>["queryFn"]>>;

/** The venue of the roster tab `tab` (out-of-range indices clamp, like the roster does). */
export function rosterTabVenue(app: App, tab = 0) {
    const tabs = app.rosterTabs;
    if (!tabs.length) {
        return undefined;
    }
    return app.getVenue(tabs[Math.min(tabs.length - 1, Math.max(0, tab))].venue) ?? undefined;
}

export const ROSTER_STALE_TIME = 30_000;

/** Warms the cache for a roster tab and range; a no-op when the user has no roster access. */
export function prefetchRoster(app: App, queryClient: QueryClient, { tab, range }: { tab?: number; range: DateRange }) {
    const venue = app.hasPermission("manage.roster") ? rosterTabVenue(app, tab) : undefined;
    if (!venue) {
        return Promise.resolve();
    }
    return queryClient.prefetchQuery({ ...rosterQuery(app, venue, range), staleTime: ROSTER_STALE_TIME });
}
